import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { toast } from '@/components/toast';
import BiggerPackagesLogo from '@/images/BiggerPackagesLogo.svg';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import Presenters from '@/images/SPPlus.png';
import PostCheckout from '@/pages/editor/components/modals/PostCheckout';
import { Billing } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { creditsToVideoMinutes } from '@/state/credits/credits';
import { useProductBiggerPrices } from '@/state/products/Products';
import { PaymentProvider } from '@/state/user-settings/types';
import theme from '@/theme';
import { PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import CloseIcon from '@mui/icons-material/Close';
import { Button, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import { ChangePlanModal } from './ChangePlanModal';
import SimpleNotificationModal from './SimpleNotificationModal';
import UpgradePlanModal from './UpgradePlanModal';

const Title = styled.div`
    margin-bottom: 0.5rem;
    margin-top: 0.8rem;
    text-align: center;
    font-style: normal;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.02em;
    margin-top: -30px;
`;

const SubTitle = styled.div`
    margin-bottom: 1.5rem;
    text-align: center;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
`;

const CreditsNeeded = styled.span`
    color: #fc5555;
    margin-left: 4px;
    font-weight: 600;
`;

const PackageDetails = styled.div<{ selected: boolean; currentPlan: boolean; prevPlan: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    width: 100%;
    height: 60px;
    margin-bottom: 10px;
    background: ${({ selected }) => (selected ? '#F2F0FF' : '#FFFEFD')};
    border: ${({ selected, theme }) => (selected ? `2px solid ${theme.colors.primary.main}` : '2px solid #E7E8EB')};
    border-radius: 12px;
    color: ${({ prevPlan }) => (prevPlan ? '#7C7C7A' : '#28282b')};
    cursor: ${({ prevPlan }) => (prevPlan ? 'not-allowed' : 'pointer')};
    pointer-events: ${({ prevPlan }) => (prevPlan ? 'none' : 'auto')};
`;

const BorderTitle = styled.div<{ show: boolean }>`
    font-size: 14px;
    margin-top: -58px;
    margin-left: 5px;
    background: white;
    padding-left: 5px;
    padding-right: 5px;
    position: absolute;
    display: ${({ show }) => (show ? 'block' : 'none')};
    text-transform: capitalize;
`;

const PackageDetailsPriceData = styled.div`
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 23px;
    display: flex;
    align-items: center;
    width: 80%;
`;

const PackageDetailsPrice = styled.div`
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    display: flex;
    align-items: center;
    width: 30%;
`;

const ButtonText = styled.div`
    font-style: normal;
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;
    text-transform: none;
`;

const StyledDialogTitle = styled(DialogTitle)`
    font-size: 1.6rem;
    justify-content: center;
    flex-direction: column;
    margin-bottom: 10px;
`;

const StyledDialogContent = styled(DialogContent, { shouldForwardProp: p => p !== 'isSpinner' })<{ isSpinner: boolean }>(
    ({ isSpinner }) => ({
        minHeight: isSpinner ? '25rem' : '10rem',
        padding: '0 40px !important',
        transition: 'min-height 0.75s ease-in',
    })
);

const StyledIconButton = styled(IconButton)`
    position: absolute;
    right: 1rem;
    color: ${theme.colors.primary};
    top: 0.5rem;
`;

const Loading = styled(Player)`
    height: 25rem;
    width: 70%;
`;

const ConfirmButton = styled(Button)<{ disabled: boolean }>(({ theme, disabled }) => ({
    padding: '0.5rem 10px',
    fontSize: '1.6rem',
    borderRadius: '8px',
    background: disabled ? theme.palette?.action?.disabledBackground : theme.colors.primary.main,
    ':hover': {
        background: disabled ? theme.palette?.action?.disabledBackground : theme.colors.primary.main,
    },
    color: 'white',
    lineHeight: 1,
    width: '180px',
    height: '42px',
}));

const StyledBootstrapDialog = styled(BootstrapDialog)<{ hide: number }>(({ hide }) => ({
    display: hide ? 'none' : 'block',
}));

const CancelButton = styled(Button)`
    border: 1px solid #3c3c3c;
    border-radius: 8px;
    padding: 0.5rem 40px;
    font-size: 1.6rem;
    border-radius: 8px;
    background: white;
    color: ${theme.colors.primary};
    line-height: 1;
    height: 42px;
    :hover {
        background: white;
    }
    width: 90px;
`;

const StyledDialogActions = styled(DialogActions)`
    margin-top: 1.75rem;
    margin-bottom: 2.5rem;
    padding: 0 40px !important;
`;

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    creditsNeeded: number;
    onUpgrade?: () => void;
}

export default function IncreasePackagesModal({ isOpen, setIsOpen, creditsNeeded, onUpgrade }: Props) {
    const { user } = useAuth();

    const userPriceId = user?.priceId!;
    const userInterval = user?.billingInterval!;
    const [selectedBiggerPrice, setSelectedBiggerPrice] = useState<string>('');
    const newPlan = user?.planGroup!;
    const newInterval = user?.billingInterval!;
    const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
    const [isPostCheckoutOpen, setIsPostCheckoutOpen] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [openMobileModal, setOpenMobileModal] = useState(false);
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);

    useEffect(() => {
        if (isOpen) {
            mixpanel.track('bigger-plan', {
                event: 'view',
                currentPrice: { id: user?.priceId, credits: user?.priceCredits },
            });
        }
    }, [isOpen]);

    if (!userPriceId || !userInterval) {
        logger.warn('user price id or interval is missing');
        return null;
    }

    const { data: biggerPackagesData, error: biggerPackagesError } = useProductBiggerPrices(userPriceId, userInterval);

    if (biggerPackagesError) {
        logger.error('failed to fetch bigger packages', biggerPackagesError);
        toast.error({ title: 'Failed to fetch bigger packages' });
    }

    const handleConfirm = async () => {
        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('goto-mobile-manage-account', {
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
        } else {
            setIsChangePlanOpen(true);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const renderPostCheckoutModal = (requestedPlan: PlanGroup, requestedBilling: Billing, newPriceId: string) => {
        if (!isPostCheckoutOpen) return null;
        return (
            <PostCheckout
                onClose={() => {
                    onUpgrade?.();
                }}
                requestedPlan={requestedPlan}
                requestedBilling={requestedBilling}
                requestedPriceId={newPriceId}
                postConfirmationText={'Generating...'}
            />
        );
    };

    const getUpgradeModal = () => {
        return (
            <UpgradePlanModal
                open={showUpgradeModal}
                onClose={() => {
                    setIsPostCheckoutOpen(false);
                    setIsOpen(false);
                }}
                title={<div>Upgrade to get more {creditsToMinutesEnabled ? 'minutes' : 'credits'}</div>}
                imageFile={Presenters}
                from="increase packages modal"
            />
        );
    };

    const renderContent = () => {
        let currentPlanCreditsAmount = biggerPackagesData?.find(
            biggerPackage => parseInt(biggerPackage.metadata.credits) === parseInt(user?.priceCredits!)
        )?.unit_amount;

        if (!currentPlanCreditsAmount) {
            currentPlanCreditsAmount = biggerPackagesData?.reduce((acc, biggerPackage) => {
                if (acc === -1 || biggerPackage.unit_amount < acc) {
                    return biggerPackage.unit_amount;
                }
                return acc;
            }, -1);
        }

        const packageDetailsList = biggerPackagesData?.map(biggerPackage => {
            const isPrevPlan = biggerPackage.unit_amount <= (currentPlanCreditsAmount || 0);
            if (!selectedBiggerPrice && !isPrevPlan) {
                setSelectedBiggerPrice(biggerPackage.id);
            }

            return {
                selected: biggerPackage.id === selectedBiggerPrice,
                text: creditsToMinutesEnabled
                    ? `${creditsToVideoMinutes(parseInt(biggerPackage.metadata.credits))} min`
                    : `${biggerPackage.metadata.credits} Credits (${creditsToVideoMinutes(parseInt(biggerPackage.metadata.credits))} min)`,
                price: biggerPackage.unit_amount / 100,
                prevPlan: isPrevPlan,
                id: biggerPackage.id,
                credits: biggerPackage.metadata.credits,
            };
        });
        const currentPlan = packageDetailsList?.find(packageDetails => packageDetails.prevPlan)?.id;
        const allPrevPlans = packageDetailsList?.every(packageDetails => packageDetails.prevPlan);
        if (allPrevPlans && !showUpgradeModal) {
            setShowUpgradeModal(true);
        }

        return !packageDetailsList || allPrevPlans ? (
            <Loading src={LoadingAnimation} autoplay loop />
        ) : (
            <>
                {packageDetailsList.reverse().map((packageDetails, index) => (
                    <PackageDetails
                        key={index}
                        onClick={() => {
                            mixpanel.track('bigger-plan', {
                                event: 'click',
                                currentPrice: { id: user?.priceId, credits: user?.priceCredits },
                                selectedPrice: { id: packageDetails.id, credits: packageDetails.credits },
                            });
                            setSelectedBiggerPrice(packageDetails.id);
                        }}
                        selected={packageDetails.selected}
                        currentPlan
                        prevPlan={packageDetails.prevPlan || false}>
                        <BorderTitle show={packageDetails.id == currentPlan}>{`Current ${
                            user?.planGroup?.split('-')[1]
                        } Plan`}</BorderTitle>
                        <PackageDetailsPriceData>{packageDetails.text}</PackageDetailsPriceData>
                        <PackageDetailsPrice>{`${packageDetails.price} / ${newInterval}`}</PackageDetailsPrice>
                    </PackageDetails>
                ))}
            </>
        );
    };

    const renderChangePlanModal = () => {
        if (!isChangePlanOpen) return null;
        return (
            <>
                <ChangePlanModal
                    setIsOpen={setIsChangePlanOpen}
                    newPlan={newPlan}
                    newInterval={newInterval}
                    newPriceId={selectedBiggerPrice}
                    openPostCheckout={() => setIsPostCheckoutOpen(true)}
                />
                <SimpleNotificationModal
                    open={openMobileModal}
                    onClose={() => {
                        setOpenMobileModal(false);
                    }}
                    content="Please use the mobile app to manage your plan."
                    primaryActionContent="Got It"
                    onPrimaryAction={() => {
                        setOpenMobileModal(false);
                    }}
                    buttonPlacement="center"
                />
            </>
        );
    };

    return (
        <StyledBootstrapDialog
            hide={isChangePlanOpen || isPostCheckoutOpen ? 1 : 0}
            onClose={handleClose}
            open={isOpen}
            width="560px"
            contentstyle={{ flex: 1, overflow: 'auto', alignItems: 'center' }}
            PaperProps={{ style: { borderRadius: '12px' } }}>
            <StyledDialogTitle id="max-packages-modal">
                <img src={BiggerPackagesLogo} alt="Bigger Packages" height={'220px'} />
                <Title>Add more {creditsToMinutesEnabled ? 'minutes' : 'credits'} to generate</Title>
                <SubTitle>
                    {creditsToMinutesEnabled ? 'minutes' : 'credits'} needed:
                    <CreditsNeeded>
                        {creditsToMinutesEnabled ? creditsToVideoMinutes(Math.abs(creditsNeeded)) : Math.abs(creditsNeeded)}
                    </CreditsNeeded>
                </SubTitle>
                <StyledIconButton aria-label="close" onClick={handleClose}>
                    <CloseIcon />
                </StyledIconButton>
            </StyledDialogTitle>
            <StyledDialogContent isSpinner={!biggerPackagesData}>{renderContent()}</StyledDialogContent>
            <StyledDialogActions>
                <CancelButton disableFocusRipple onClick={handleClose}>
                    <ButtonText>Cancel</ButtonText>
                </CancelButton>
                <ConfirmButton disableFocusRipple onClick={handleConfirm} disabled={!biggerPackagesData}>
                    <ButtonText>Proceed to Checkout</ButtonText>
                </ConfirmButton>
            </StyledDialogActions>
            {renderChangePlanModal()}
            {renderPostCheckoutModal(newPlan as PlanGroup, newInterval as Billing, selectedBiggerPrice)}
            {getUpgradeModal()}
        </StyledBootstrapDialog>
    );
}
